// src/hooks/useFinanceData.js
import {useState, useEffect, useCallback} from "react"
import {ref, onValue, get, push, update, remove, set} from "firebase/database"
import {auth, database} from "../firebase"
import moment from "moment"
import mockData from "../data/mockData" // Import the mock data
import {formatSmartCurrency} from "../utils/formatCurrency"
import {getCurrency} from "../utils/currencyConfig"
export const useFinanceData = () => {
    const [cashFlowItems, setCashFlowItems] = useState([])
    const [projectedBalances, setProjectedBalances] = useState({})
    const [balances, setBalances] = useState({})
    const [budgets, setBudgets] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [isAuthenticated, setIsAuthenticated] = useState(false)

    // Add these new state variables to match the React Native version
    const [currentTotalBalance, setCurrentTotalBalance] = useState(0)
    const [hasAnyBalances, setHasAnyBalances] = useState(false)
    const [lastUpdateOrCreatedTime, setLastUpdateOrCreatedTime] = useState(null)

    // Get current total balance across accounts included in projections
    const getCurrentBalance = useCallback(() => {
        return cashFlowItems
            .filter((item) => item.type === "balance" && item.includeInProjections !== false)
            .reduce((total, item) => total + item.amount, 0)
    }, [cashFlowItems])

    // Currency-aware format function
    // Reads user preference from localStorage (synced by CurrencyContext)
    const formatAmount = useCallback((amount, options = {}) => {
      const {
        showSign = true,
        format = 'short',
      } = options;

      const currencyCode = localStorage.getItem('forecast_currency') || 'USD';
      const roundForDisplay = localStorage.getItem('forecast_round_decimals') === 'true';

      return formatSmartCurrency(Number(amount), {
        mode: format === 'long' ? 'long' : 'short',
        showSign: showSign && Number(amount) < 0,
        currencyCode,
        roundForDisplay,
      });
    }, []);

const getItemsForDateRange = useCallback((startDate, endDate) => {
    const start = moment(startDate).startOf('day');
    const end = moment(endDate).endOf('day');
  
    return cashFlowItems.flatMap(item => {
      const occurrences = [];
  
      if (item.deleted) return [];
  
      if (item.endDate && moment(item.endDate).isBefore(start)) return [];
  
      // Handle occurrence exceptions first
      if (item.occurrenceExceptions) {
        Object.entries(item.occurrenceExceptions).forEach(([exceptionDate, exception]) => {
          // Skip deleted exceptions
          if (exception.deleted) return;
          
          // If this is a moved exception, we'll handle it at the new date
          if (exception.movedFrom) return;
          
          const exceptionMoment = moment(exceptionDate);
          if (exceptionMoment.isBetween(start, end, 'day', '[]')) {
            occurrences.push({
              ...item,
              ...exception,
              date: exception.date || exceptionMoment.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
              dueDate: exceptionMoment.format('YYYY-MM-DD'),
              isRecurring: true,
              originalId: item.id,
              occurrenceDate: exceptionDate,
              isException: true,
              amount: parseFloat((exception.amount/100).toFixed(2)), // Convert to dollars
              isPaid: exception.isPaid || false,
            });
          }
        });
        
        // Handle moved exceptions - add them at their new dates
        Object.entries(item.occurrenceExceptions).forEach(([exceptionDate, exception]) => {
          // Only process exceptions that were moved to a new date
          if (!exception.movedFrom) return;
          
          const exceptionMoment = moment(exceptionDate);
          if (exceptionMoment.isBetween(start, end, 'day', '[]')) {
            occurrences.push({
              ...item,
              ...exception,
              date: exception.date || exceptionMoment.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
              dueDate: exceptionMoment.format('YYYY-MM-DD'),
              isRecurring: true,
              originalId: item.id,
              occurrenceDate: exceptionDate,
              movedFrom: exception.movedFrom,
              isException: true,
              amount: parseFloat((exception.amount/100).toFixed(2)), // Convert to dollars
              isPaid: exception.isPaid || false,
            });
          }
        });
      }
  
      // Handle regular recurrences
      if (item.recurrenceFrequency && item.recurrenceFrequency !== 'none') {
        // Important: Always use the original start date for the sequence
        let currentDate = moment(item.date);
        const effectiveEndDate = item.endDate ? moment.min(moment(item.endDate), end) : end;
  
        while (currentDate.isSameOrBefore(effectiveEndDate)) {
          // Skip dates that have exceptions (deleted or modified)
          const dueDate = currentDate.format('YYYY-MM-DD');
          
          if (!item.occurrenceExceptions?.[dueDate]) {
            let occurrenceItem = { ...item };
            let displayDate = moment(currentDate); // Create a copy for display purposes
            let displayDueDate = dueDate;
  
            // Check if this occurrence should use future edits
            const shouldUseFutureEdits = item.futureEdits && 
                                        item.editedFromDate && 
                                        moment(dueDate).isSameOrAfter(item.editedFromDate);
            
            // Apply future edits if applicable
            if (shouldUseFutureEdits) {
              occurrenceItem = {
                ...occurrenceItem,
                ...item.futureEdits,
                // Important: Keep original sequence start date for the base item
                date: item.date
              };
              
              // Apply day offset for date pattern changes if present
              if (item.futureEdits.dayOffset) {
                displayDate = moment(currentDate).add(item.futureEdits.dayOffset, 'days');
                displayDueDate = displayDate.format('YYYY-MM-DD');
              }
            }
  
            // Only add to occurrences if the adjusted date is in our range
            if (displayDate.isBetween(start, end, 'day', '[]')) {
              occurrenceItem = {
                ...occurrenceItem,
                date: displayDate.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
                dueDate: displayDueDate,
                isRecurring: true,
                originalId: item.id,
                occurrenceDate: dueDate, // Keep track of the original occurrence date
                // Handle amount conversion appropriately
                amount: shouldUseFutureEdits
                  ? parseFloat((occurrenceItem.amount / 100).toFixed(2))
                  : parseFloat((occurrenceItem.amount).toFixed(2)),
                isPaid: false,
                isPastDue: moment().isAfter(displayDate, 'day'),
              };
  
              occurrences.push(occurrenceItem);
            }
          }
  
          // Use appropriate recurrence pattern for date advancement
          const pattern = item.futureEdits && currentDate.isSameOrAfter(moment(item.editedFromDate))
            ? item.futureEdits
            : item;
  
          switch (pattern.recurrencePeriod) {
            case 'days':
              currentDate.add(parseInt(pattern.recurrenceFrequency), 'days');
              break;
            case 'weeks':
              currentDate.add(parseInt(pattern.recurrenceFrequency), 'weeks');
              break;
            case 'months':
              currentDate.add(parseInt(pattern.recurrenceFrequency), 'months');
              break;
            case 'years':
              currentDate.add(parseInt(pattern.recurrenceFrequency), 'years');
              break;
          }
        }
      } else if (moment(item.date).isBetween(start, end, 'day', '[]')) {
        return [{
          ...item,
          dueDate: moment(item.date).format('YYYY-MM-DD'),
          amount: parseFloat((item.amount).toFixed(2)),
          isPaid: item.isPaid || false,
          isPastDue: !item.isPaid && moment().isAfter(moment(item.date), 'day'),
        }];
      }
  
      return occurrences;
    });
  }, [cashFlowItems]);
    // Helper function to get the next occurrence based on recurrence pattern
    const getNextOccurrence = (date, period, frequency) => {
        const newDate = new Date(date)

        switch (period) {
            case "days":
                newDate.setDate(newDate.getDate() + frequency)
                break
            case "weeks":
                newDate.setDate(newDate.getDate() + 7 * frequency)
                break
            case "months":
                newDate.setMonth(newDate.getMonth() + frequency)
                break
            case "years":
                newDate.setFullYear(newDate.getFullYear() + frequency)
                break
            default:
                console.error("Unknown recurrence period:", period)
        }

        return newDate
    }

    // Generate recurring events for a date range
    const generateRecurringEvents = useCallback((item, startDate, endDate) => {
        const events = []
        let currentDate = new Date(item.date)
        const end = new Date(endDate)
        const frequency = parseInt(item.recurrenceFrequency) || 1

        // Consider occurrence exceptions
        const exceptions = item.occurrenceExceptions || {}

        while (currentDate <= end) {
            const dateStr = currentDate.toISOString().split("T")[0]

            // Check if this date is within our range
            if (currentDate >= startDate && currentDate <= endDate) {
                // Check if there's an exception for this date
                if (exceptions[dateStr]) {
                    const exception = exceptions[dateStr]

                    // If the exception is marked as deleted, skip this occurrence
                    if (exception.deleted) {
                        currentDate = getNextOccurrence(
                            currentDate,
                            item.recurrencePeriod,
                            frequency
                        )
                        continue
                    }

                    // Otherwise, use the exception data
                    events.push({
                        ...item,
                        ...exception,
                        date: new Date(exception.date || dateStr),
                        dueDate: dateStr,
                        isRecurring: true,
                        originalId: item.id,
                        occurrenceDate: dateStr,
                        isException: true,
                        // For expenses stored in cents, convert to negative dollars
                        amount:
                            item.type === "expense"
                                ? -Math.abs(exception.amount / 100)
                                : exception.amount / 100,
                        isPaid: exception.isPaid || false,
                    })
                } else {
                    // Regular occurrence, no exception
                    events.push({
                        ...item,
                        date: new Date(currentDate),
                        dueDate: dateStr,
                        isRecurring: true,
                        originalId: item.id,
                        occurrenceDate: dateStr,
                        isPaid: item.isPaid || false,
                        isPastDue: !item.isPaid && new Date() > currentDate,
                    })
                }
            }

            // Move to next occurrence
            currentDate = getNextOccurrence(
                currentDate,
                item.recurrencePeriod,
                frequency
            )
        }

        return events
    }, [])

    // Calculate projected daily balances
    // Calculate projected daily balances
const calculateProjectedBalances = useCallback(() => {
  try {
      // Prevent calculation if data isn't ready
      if (cashFlowItems.length === 0) {
          return {}
      }

      // Start with today's date (beginning of day)
      const startDate = new Date()
      startDate.setHours(0, 0, 0, 0)

      const endDate = new Date()
      endDate.setMonth(endDate.getMonth() + 3) // Look ahead 3 months

      // Get current balance
      const currentBalance = getCurrentBalance() * 100 // Convert to cents for calculations

      // Get all relevant transactions in the date range
      const transactions = getItemsForDateRange(startDate, endDate)
          .filter(
              (item) => item.type === "income" || item.type === "expense"
          )
          .sort((a, b) => new Date(a.date) - new Date(b.date))

      // Calculate balances ONLY for days with transactions
      const dailyBalances = {}
      let runningBalance = currentBalance

      // Always add today's starting balance, even if there are no transactions
      const todayStr = startDate.toISOString().split("T")[0]
      dailyBalances[todayStr] = runningBalance

      // Set to track days we've already processed
      const processedDates = new Set([todayStr])

      transactions.forEach((transaction) => {
          const dateStr = new Date(transaction.date)
              .toISOString()
              .split("T")[0]

          // Skip past transactions that are already paid
          if (transaction.isPaid) return

          // Add this date to processed dates set
          processedDates.add(dateStr)

          // For income, add to balance; for expenses, subtract
          if (transaction.type === "income") {
              runningBalance += Math.abs(transaction.amount) * 100 // Convert back to cents
          } else if (transaction.type === "expense") {
              runningBalance -= Math.abs(transaction.amount) * 100 // Convert back to cents
          }

          // Store the balance for this date
          dailyBalances[dateStr] = runningBalance
      })

      console.log('Daily Balances (only for days with transactions):', dailyBalances);

      setProjectedBalances(dailyBalances)
      return dailyBalances
  } catch (error) {
      console.error("Error calculating projected balances:", error)
      return {}
  }
}, [cashFlowItems, getCurrentBalance, getItemsForDateRange])
    // Calculate monthly income
    const calculateMonthlyIncome = useCallback(() => {
        const incomeItems = cashFlowItems.filter(
            (item) => item.type === "income"
        )
        let monthlyTotal = 0

        incomeItems.forEach((item) => {
            if (!item.recurrenceFrequency || item.recurrencePeriod === "none") {
                // One-time income, convert to monthly equivalent
                const itemDate = new Date(item.date)
                const currentMonth = new Date().getMonth()
                const currentYear = new Date().getFullYear()

                if (
                    itemDate.getMonth() === currentMonth &&
                    itemDate.getFullYear() === currentYear
                ) {
                    monthlyTotal += item.amount
                }
            } else {
                // Recurring income
                const frequency = parseInt(item.recurrenceFrequency) || 1

                switch (item.recurrencePeriod) {
                    case "days":
                        monthlyTotal += (item.amount * 30) / frequency // Approx 30 days per month
                        break
                    case "weeks":
                        monthlyTotal += (item.amount * 4.33) / frequency // Approx 4.33 weeks per month
                        break
                    case "months":
                        monthlyTotal += item.amount / frequency
                        break
                    case "years":
                        monthlyTotal += item.amount / (12 * frequency)
                        break
                }
            }
        })

        return monthlyTotal
    }, [cashFlowItems])

    // Calculate monthly expenses
    const calculateMonthlyExpenses = useCallback(() => {
        const expenseItems = cashFlowItems.filter(
            (item) => item.type === "expense"
        )
        let monthlyTotal = 0

        expenseItems.forEach((item) => {
            if (!item.recurrenceFrequency || item.recurrencePeriod === "none") {
                // One-time expense, convert to monthly equivalent
                const itemDate = new Date(item.date)
                const currentMonth = new Date().getMonth()
                const currentYear = new Date().getFullYear()

                if (
                    itemDate.getMonth() === currentMonth &&
                    itemDate.getFullYear() === currentYear
                ) {
                    monthlyTotal += Math.abs(item.amount)
                }
            } else {
                // Recurring expense
                const frequency = parseInt(item.recurrenceFrequency) || 1

                switch (item.recurrencePeriod) {
                    case "days":
                        monthlyTotal += (Math.abs(item.amount) * 30) / frequency // Approx 30 days per month
                        break
                    case "weeks":
                        monthlyTotal +=
                            (Math.abs(item.amount) * 4.33) / frequency // Approx 4.33 weeks per month
                        break
                    case "months":
                        monthlyTotal += Math.abs(item.amount) / frequency
                        break
                    case "years":
                        monthlyTotal += Math.abs(item.amount) / (12 * frequency)
                        break
                }
            }
        })

        return monthlyTotal
    }, [cashFlowItems])

    // Calculate remaining income after expenses
    const remainingIncomeAmount = useCallback(() => {
        return calculateMonthlyIncome() - calculateMonthlyExpenses()
    }, [calculateMonthlyIncome, calculateMonthlyExpenses])

    // Update cash flow items while maintaining uniqueness
    const updateCashFlowItems = useCallback((newItems) => {
        setCashFlowItems((prevItems) => {
            const itemsMap = new Map(prevItems.map((item) => [item.id, item]))

            newItems.forEach((newItem) => {
                itemsMap.set(newItem.id, newItem)
            })

            return Array.from(itemsMap.values())
        })
    }, [])

    // Format data items with common structure and handle type-specific transformations
    const formatDataItems = (data, type) => {
        if (!data) return []

        return Object.entries(data).map(([id, item]) => {
            // Base transformation for all types
            const baseItem = {
                id,
                ...item,
                type,
                // For display purposes, convert cents to dollars
                amount:
                    type === "income"
                        ? item.amount / 100
                        : type === "expense"
                        ? -Math.abs(item.amount / 100)
                        : item.amount / 100,
            }

            // Add specific handling for recurring items and exceptions
            if (
                item.recurrenceFrequency &&
                item.recurrencePeriod &&
                item.recurrencePeriod !== "none"
            ) {
                baseItem.isRecurring = true
            }

            return baseItem
        })
    }
    // Add new function to update account balance
    const addItem = useCallback(
        async (item) => {
            const user = auth.currentUser
            if (!user) {
                console.error("No user logged in")
                return
            }

            try {
                const userId = user.uid

                // Check if user is hitting item limit (only for non-subscribed users)
                const countableItems = cashFlowItems.filter(
                    (item) =>
                        item.type === "income" ||
                        item.type === "expense" ||
                        item.type === "balance"
                ).length

                // Define item limit for free users (same as in RN version)
                const ITEM_LIMIT = 100

                // Check if user is subscribed
                const subscriptionRef = ref(
                    database,
                    `users/${userId}/subscription`
                )
                const subscriptionSnapshot = await get(subscriptionRef)
                const isSubscribed =
                    subscriptionSnapshot.exists() &&
                    subscriptionSnapshot.val()?.isSubscribed === true

                if (!isSubscribed && countableItems >= ITEM_LIMIT) {
                    console.error("Item limit reached for free user")
                    // You could emit an event here or return a special code
                    // In the web version we'll just throw an error with a specific message
                    throw new Error("ITEM_LIMIT_REACHED")
                }

                // Special handling for savings items
                if (item.type === "savings") {
                    // Calculate period dates directly instead of using state variables
                    const currentDate = new Date()
                    const firstDayOfMonth = new Date(
                        currentDate.getFullYear(),
                        currentDate.getMonth(),
                        1
                    )
                    const firstDayOfNextMonth = new Date(
                        currentDate.getFullYear(),
                        currentDate.getMonth() + 1,
                        1
                    )

                    const periodStartStr = firstDayOfMonth
                        .toISOString()
                        .split("T")[0]
                    const periodEndStr = firstDayOfNextMonth
                        .toISOString()
                        .split("T")[0]
                    const savingsId = `savings_${periodStartStr}_${periodEndStr}`

                    const savingsRef = ref(
                        database,
                        `users/${userId}/flo/savings/${savingsId}`
                    )

                    // Check if a savings entry already exists for this period
                    const snapshot = await get(savingsRef)
                    if (snapshot.exists()) {
                        // Update existing entry
                        await update(savingsRef, {
                            amount: Math.round(parseFloat(item.amount)),
                            updatedAt: new Date().toISOString(),
                            periodStart: periodStartStr,
                            periodEnd: periodEndStr,
                        })
                    } else {
                        // Create new entry
                        await set(savingsRef, {
                            amount: Math.round(parseFloat(item.amount)),
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString(),
                            periodStart: periodStartStr,
                            periodEnd: periodEndStr,
                        })
                    }

                    const savingsItem = {
                        id: savingsId,
                        type: "savings",
                        amount: parseFloat(item.amount),
                        periodStart: periodStartStr,
                        periodEnd: periodEndStr,
                    }

                    updateCashFlowItems([savingsItem])
                    calculateProjectedBalances()
                    return savingsItem
                }

                // Validate amount
                const amount = parseFloat(item.amount)
                if (isNaN(amount)) {
                    console.error("Invalid amount:", item.amount)
                    throw new Error("Invalid amount")
                }

                // Set default reminder for expenses
                let defaultReminder = null
                if (item.type === "expense") {
                    defaultReminder = {
                        enabled: true,
                        days: 1,
                        time: "09:00",
                    }
                }

                const itemToSave = {
                    ...item,
                    amount: Math.round(item.amount * 100), // Convert dollars to cents for storage
                    date: item.date
                        ? new Date(item.date).toISOString().split("T")[0]
                        : null,
                    isPaid: item.isPaid || false,
                    reminder: item.reminder || defaultReminder,
                    createdAt: new Date().toISOString(),
                }

                // Handle budget category for expenses
                if (item.type === "expense") {
                    // If budgetCategoryId is provided, use it
                    if (item.budgetCategoryId) {
                        itemToSave.budgetCategoryId = item.budgetCategoryId
                    }
                    // Otherwise, try to get or create one based on budgetCategory
                    else if (item.budgetCategory) {
                        // Call our getOrCreateBudgetCategory function if it exists
                        // Just use the id directly in this web implementation
                        itemToSave.budgetCategoryId = item.budgetCategory
                    }
                }

                // Clean up properties based on item type
                if (item.type === "expense" || item.type === "income") {
                    delete itemToSave.color
                }

                // Get the correct path for the item type
                const getItemPath = (type) => {
                    switch (type) {
                        case "income":
                            return "income"
                        case "expense":
                            return "expenses"
                        case "balance":
                            return "balances"
                        case "budget":
                            return "budgets"
                        case "savings":
                            return "savings"
                        default:
                            throw new Error(`Invalid item type: ${type}`)
                    }
                }

                const path = `users/${userId}/flo/${getItemPath(item.type)}`
                const itemRef = ref(database, path)

                let newItemRef
                // Handle budget items differently if needed
                if (item.type === "budget") {
                    newItemRef = push(itemRef)
                    await set(newItemRef, itemToSave)
                } else {
                    newItemRef = await push(itemRef, itemToSave)
                }

                const newItem = {
                    ...itemToSave,
                    id: newItemRef.key,
                    // Format the amount based on type for display
                    amount:
                        item.type === "balance"
                            ? Math.abs(itemToSave.amount / 100)
                            : item.type === "income"
                            ? itemToSave.amount / 100
                            : -Math.abs(itemToSave.amount / 100),
                }

                console.log("New item added successfully:", newItem)
                updateCashFlowItems([newItem])
                calculateProjectedBalances()

                return newItem
            } catch (error) {
                console.error("Error adding item:", error)
                throw error
            }
        },
        [calculateProjectedBalances, updateCashFlowItems, cashFlowItems]
    )
// In useFinanceData.js, modify the editItem function to properly handle income items
// by checking the item type before trying to include budgetCategoryId
// In useFinanceData.js, modify the editItem function to properly handle income items
// by setting reminder to null for income items

// In useFinanceData.js, modify the editItem function to properly handle income items
// by setting reminder to null for income items

const editItem = useCallback(
    async (editedItem, editType = 'all') => {
      const user = auth.currentUser;
      if (!user) return;
      console.log('editing item!', editedItem);
    
      // Add this line to define userId
      const userId = user.uid;
      const getItemPath = (type) => {
        switch (type) {
            case "income":
                return "income";
            case "expense":
                return "expenses";
            case "balance":
                return "balances";
            case "budget":
                return "budgets";
            case "savings":
                return "savings";
            default:
                throw new Error(`Invalid item type: ${type}`);
        }
      };
  
      // Get the item path based on type
      const itemRef = ref(
        database,
        `users/${userId}/flo/${getItemPath(editedItem.type)}/${editedItem.id}`
      );
    
      try {
        const snapshot = await get(itemRef);
        const currentItem = snapshot.val();
        if (!currentItem) throw new Error("Item not found");
    
        let updates = {};
    
        if (editType === "notification-only") {
          updates = { ...currentItem };
          
          // Only update notificationPreference for expense items
          if (editedItem.type === "expense") {
            updates.notificationPreference = editedItem.notificationPreference;
          } else {
            // For income items, explicitly set to null
            updates.notificationPreference = null;
          }
        } else {
          switch (editType) {
            case "single": {
              // Get the actual occurrence date we're modifying
              const occurrenceDate = moment(
                editedItem.occurrenceDate || editedItem.date
              ).format("YYYY-MM-DD");
              const newDate = moment(editedItem.date).format("YYYY-MM-DD");
    
              console.log("Editing occurrence:", {
                occurrenceDate,
                newDate,
                originalDate: currentItem.date,
                amount: editedItem.amount,
              });
    
              // Create new exceptions object starting with existing exceptions
              const updatedExceptions = {
                ...currentItem.occurrenceExceptions,
              };
    
              // Handle date pattern change for single occurrence
              if (editedItem.datePatternChange && newDate !== occurrenceDate) {
                // Mark original date as deleted
                updatedExceptions[occurrenceDate] = {
                  deleted: true,
                  deletedAt: new Date().toISOString(),
                };
    
                // Create new occurrence at new date with type-specific fields
                const newException = {
                  title: editedItem.title,
                  amount: Math.round(
                    Math.abs(editedItem.amount) * 100
                  ), // Convert dollars to cents for storage
                  emoji: editedItem.emoji,
                  date: editedItem.date,
                  isPaid: editedItem.isPaid || false,
                  movedFrom: occurrenceDate,
                };
                
                // Handle type-specific fields
                if (editedItem.type === "expense") {
                  if (editedItem.budgetCategoryId) {
                    newException.budgetCategoryId = editedItem.budgetCategoryId;
                  }
                  if (editedItem.reminder) {
                    newException.reminder = editedItem.reminder;
                  }
                } else if (editedItem.type === "income") {
                  // Explicitly set reminder to null for income
                  newException.reminder = null;
                  // Ensure budgetCategoryId is not included
                  newException.budgetCategoryId = null;
                }
                
                updatedExceptions[newDate] = newException;
              } else {
                // Create or update the exception for this occurrence
                const updatedException = {
                  title: editedItem.title,
                  amount: Math.round(
                    Math.abs(editedItem.amount) * 100
                  ), // Convert dollars to cents for storage
                  emoji: editedItem.emoji,
                  date: editedItem.date,
                  isPaid: editedItem.isPaid || false,
                };
                
                // Handle type-specific fields
                if (editedItem.type === "expense") {
                  if (editedItem.budgetCategoryId) {
                    updatedException.budgetCategoryId = editedItem.budgetCategoryId;
                  }
                  if (editedItem.reminder) {
                    updatedException.reminder = editedItem.reminder;
                  }
                } else if (editedItem.type === "income") {
                  // Explicitly set reminder to null for income
                  updatedException.reminder = null;
                  // Ensure budgetCategoryId is not included
                  updatedException.budgetCategoryId = null;
                }
                
                updatedExceptions[occurrenceDate] = updatedException;
              }
    
              updates = {
                ...currentItem,
                occurrenceExceptions: updatedExceptions,
              };
              break;
            }
    
            case "future": {
              const editStartDate = moment(
                editedItem.occurrenceDate || editedItem.date
              );
    
              // Get the original occurrence date we're modifying
              const originalOccurrenceDate = moment(editedItem.occurrenceDate || editedItem.date);
              
              // Get the new date for this occurrence
              const newOccurrenceDate = moment(editedItem.date);
              
              // Calculate the day difference to adjust future occurrences
              const dayDiff = newOccurrenceDate.diff(originalOccurrenceDate, 'days');
              
              // Clean up the editedItem object to keep only relevant properties
              const cleanEditedItem = {
                title: editedItem.title,
                amount: Math.round(Math.abs(editedItem.amount) * 100), // Convert to cents
                emoji: editedItem.emoji,
                recurrenceFrequency: editedItem.recurrenceFrequency,
                recurrencePeriod: editedItem.recurrencePeriod,
                date: currentItem.date, // Keep original start date
                startedFrom: editStartDate.format("YYYY-MM-DD"),
              };
              
              // Only add notificationPreference for expense items
              if (editedItem.type === "expense" && editedItem.notificationPreference) {
                cleanEditedItem.notificationPreference = editedItem.notificationPreference;
              }
              
              // Handle type-specific fields
              if (editedItem.type === "expense") {
                if (editedItem.budgetCategoryId) {
                  cleanEditedItem.budgetCategoryId = editedItem.budgetCategoryId;
                }
                if (editedItem.reminder) {
                  cleanEditedItem.reminder = editedItem.reminder;
                }
              } else if (editedItem.type === "income") {
                // Explicitly set fields to null for income items
                cleanEditedItem.reminder = null;
                cleanEditedItem.budgetCategoryId = null;
                cleanEditedItem.notificationPreference = null;
              }
              
              // Add dayOffset for date pattern changes
              if (dayDiff !== 0) {
                cleanEditedItem.dayOffset = dayDiff;
              }
    
              updates = {
                ...currentItem,
                editedFromDate: editStartDate.format("YYYY-MM-DD"),
                futureEdits: cleanEditedItem,
                // Preserve exceptions for past occurrences only
                occurrenceExceptions: Object.entries(
                  currentItem.occurrenceExceptions || {}
                )
                  .filter(([date]) =>
                    moment(date).isBefore(editStartDate)
                  )
                  .reduce(
                    (acc, [date, value]) => ({
                      ...acc,
                      [date]: value,
                    }),
                    {}
                  ),
              };
              break;
            }
    
            case "all": {
              // For complete sequence updates, always preserve the original start date
              // when recurrence is being modified or for any recurring item
              const isRecurring =
                currentItem.recurrenceFrequency !== "none" &&
                currentItem.recurrencePeriod !== "none";
    
              // If date pattern is changing for all occurrences,
              // we actually need to reset the base date
              const newStartDate = editedItem.datePatternChange ? 
                editedItem.date :
                (isRecurring ? currentItem.date : editedItem.date);
    
              // Start with required common fields for all item types
              updates = {
                title: editedItem.title,
                amount: Math.round(Math.abs(editedItem.amount) * 100),
                date: newStartDate,
                emoji: editedItem.emoji,
                recurrenceFrequency: editedItem.recurrenceFrequency,
                recurrencePeriod: editedItem.recurrencePeriod,
                // Reset all exceptions and future edits
                occurrenceExceptions: {},
                futureEdits: null,
                editedFromDate: null,
                updatedAt: new Date().toISOString(),
                // Keep existing createdAt
                createdAt: currentItem.createdAt || new Date().toISOString()
              };
              
              // Handle type-specific fields
              if (editedItem.type === "expense") {
                if (editedItem.budgetCategoryId) {
                  updates.budgetCategoryId = editedItem.budgetCategoryId;
                }
                if (editedItem.reminder) {
                  updates.reminder = editedItem.reminder;
                }
              } else if (editedItem.type === "income") {
                // Explicitly set fields to null for income items
                updates.reminder = null;
                updates.budgetCategoryId = null;
                updates.notificationPreference = null;
              }
              
              break;
            }
          }
        }
    
        // For a clean update, we need to use a new object instead of modifying currentItem
        await update(itemRef, updates);
    
        // Update local state with the edited item
        const formattedUpdates = {
          ...updates,
          id: editedItem.id,
          type: editedItem.type,
          // Format the amount for display
          amount:
            editedItem.type === "expense"
              ? -Math.abs(updates.amount / 100)
              : updates.amount / 100,
        };
    
        updateCashFlowItems([formattedUpdates]);
        calculateProjectedBalances();
    
        return updates;
      } catch (error) {
        console.error("Error updating item:", error);
        throw error;
      }
    },
    [auth, calculateProjectedBalances, updateCashFlowItems]
  );
    // Add function to delete an item
    const deleteItem = useCallback(
        async (item, deleteType = 'all', deleteDate = null) => {
          const user = auth.currentUser;
          if (!user) return;
      
          console.log('deleteItem received:', {
            itemId: item.id,
            deleteType,
            deleteDate,
            recurrencePeriod: item.recurrencePeriod,
            type: item.type,
            fullItem: item,
          });
          
          // Debug: Check if item.type exists and its value
          if (!item.type) {
            console.error('CRITICAL ERROR: item.type is undefined or null', item);
            // Try to infer type from other properties if possible
            if (item.budgetCategoryId) {
              console.log('Inferring type as "expense" based on budgetCategoryId property');
              item.type = 'expense';
            } else if (item.amount > 0 && !item.budgetCategoryId) {
              console.log('Inferring type as "income" based on positive amount and no budget category');
              item.type = 'income';
            } else {
              console.log('Could not infer item type, properties available:', Object.keys(item));
            }
          }
      
          const getItemPath = (type) => {
            switch (type) {
              case "income":
                return "income";
              case "expense":
                return "expenses";
              case "balance":
                return "balances";
              case "budget":
                return "budgets";
              case "savings":
                return "savings";
              default:
                throw new Error(`Invalid item type: ${type}`);
            }
          };
      
          const itemPath = getItemPath(item.type);
          const userId = user.uid;
          const itemRef = ref(database, `users/${userId}/flo/${itemPath}/${item.id}`);
      
          try {
            // Get current item data
            const snapshot = await get(itemRef);
            const currentItem = snapshot.val();
      
            console.log('Current item from database:', {
              id: item.id,
              recurrencePeriod: currentItem?.recurrencePeriod,
              fullItem: currentItem,
            });
      
            if (!currentItem) {
              console.error('Item not found');
              return;
            }
      
            // Handle non-recurring items or 'all' deletion type
            if (currentItem.recurrencePeriod === 'none' || deleteType === 'all') {
              console.log('Deleting entire item because:', {
                isNonRecurring: currentItem.recurrencePeriod === 'none',
                deleteTypeIsAll: deleteType === 'all',
              });
              await remove(itemRef);
              setCashFlowItems(prevItems => prevItems.filter(i => i.id !== item.id));
              calculateProjectedBalances();
              return;
            }
      
            // For recurring items, handle based on deleteType
            console.log('Processing recurring item deletion:', { deleteType });
      
            switch (deleteType) {
              case 'single': {
                console.log('Processing single occurrence deletion for date:', deleteDate);
                const occurrenceDate = moment(deleteDate).format('YYYY-MM-DD');
                const updates = {
                  occurrenceExceptions: {
                    ...(currentItem.occurrenceExceptions || {}),
                    [occurrenceDate]: {
                      deleted: true,
                      deletedAt: new Date().toISOString(),
                    },
                  },
                };
                await update(itemRef, updates);
      
                setCashFlowItems(prevItems =>
                  prevItems.map(i =>
                    i.id === item.id
                      ? {
                          ...i,
                          occurrenceExceptions: {
                            ...(i.occurrenceExceptions || {}),
                            [occurrenceDate]: updates.occurrenceExceptions[occurrenceDate],
                          },
                        }
                      : i
                  )
                );
                break;
              }
      
              case 'future': {
                console.log('Processing future occurrences deletion from date:', deleteDate);
                const deleteMoment = moment(deleteDate);
                
                // Check if this is the first occurrence
                const firstOccurrenceDate = moment(currentItem.date).format('YYYY-MM-DD');
                const deleteOccurrenceDate = deleteMoment.format('YYYY-MM-DD');
                
                // If deleting from the first occurrence, just delete the whole item
                if (firstOccurrenceDate === deleteOccurrenceDate) {
                  console.log('Deleting from first occurrence, removing entire item');
                  await remove(itemRef);
                  setCashFlowItems(prevItems => prevItems.filter(i => i.id !== item.id));
                  calculateProjectedBalances();
                  return;
                }
                
                // Check if this is the second occurrence
                let secondOccurrenceDate;
                const recFreq = parseInt(currentItem.recurrenceFrequency);
                
                switch (currentItem.recurrencePeriod) {
                  case 'days':
                    secondOccurrenceDate = moment(currentItem.date).add(recFreq, 'days');
                    break;
                  case 'weeks':
                    secondOccurrenceDate = moment(currentItem.date).add(recFreq, 'weeks');
                    break;
                  case 'months':
                    secondOccurrenceDate = moment(currentItem.date).add(recFreq, 'months');
                    break;
                  case 'years':
                    secondOccurrenceDate = moment(currentItem.date).add(recFreq, 'years');
                    break;
                }
                
                const isSecondOccurrence = secondOccurrenceDate && 
                                           secondOccurrenceDate.format('YYYY-MM-DD') === deleteOccurrenceDate;
                
                if (isSecondOccurrence) {
                  console.log('Deleting from second occurrence, converting to non-recurring item');
                  // Convert to non-recurring by updating the recurrence fields
                  const updates = {
                    recurrencePeriod: 'none',
                    recurrenceFrequency: '',
                    updatedAt: new Date().toISOString(),
                  };
                  await update(itemRef, updates);
                  
                  setCashFlowItems(prevItems => 
                    prevItems.map(i => (i.id === item.id ? { ...i, ...updates } : i))
                  );
                } else {
                  // Regular case - just set the end date
                  const endDate = moment(deleteDate).subtract(1, 'day').format('YYYY-MM-DD');
                  const updates = {
                    endDate,
                    updatedAt: new Date().toISOString(),
                  };
                  await update(itemRef, updates);
      
                  setCashFlowItems(prevItems => 
                    prevItems.map(i => (i.id === item.id ? { ...i, ...updates } : i))
                  );
                }
                break;
              }
      
              default:
                console.error('Invalid delete type:', deleteType);
                return;
            }
      
            calculateProjectedBalances();
          } catch (error) {
            console.error('Error deleting item:', error);
            throw error;
          }
        },
        [calculateProjectedBalances]
      );

    // Fetch all financial data when the component mounts
    useEffect(() => {
        const fetchData = async () => {
            try {
                const user = auth.currentUser
                if (!user) {
                  // User is not authenticated, use mock data
                  setIsAuthenticated(false)
                  
                  // Format the mock data
                  const mockIncome = formatDataItems(mockData.income, "income")
                  const mockExpenses = formatDataItems(mockData.expenses, "expense")
                  const mockBalances = formatDataItems(mockData.balances, "balance")
                  const mockBudgets = formatDataItems(mockData.budgets, "budget")
                  
                  // Set all the data states
                  setCashFlowItems([...mockIncome, ...mockExpenses, ...mockBalances, ...mockBudgets])
                  setBudgets(mockBudgets)
                  
                  // Also set balances for direct access
                  const balancesObject = {}
                  Object.entries(mockData.balances).forEach(([id, balance]) => {
                      balancesObject[id] = {
                          ...balance,
                          id,
                          amount: balance.amount / 100, // Convert cents to dollars for display
                      }
                  })
                  setBalances(balancesObject)
                  
                  // Calculate total balance from mock accounts
                  const total = Object.values(mockData.balances).reduce(
                      (sum, account) => {
                          return sum + (account.amount || 0)
                      },
                      0
                  )
                  
                  // Find most recent update or creation time from mock data
                  const mostRecentTime = Object.values(
                      mockData.balances
                  ).reduce((latest, account) => {
                      const accountTime = account.lastUpdated || account.createdAt
                      if (!latest) return accountTime
                      return new Date(accountTime) > new Date(latest)
                          ? accountTime
                          : latest
                  }, null)
                  
                  // Update state values needed by CurrentBalanceHeader
                  setCurrentTotalBalance(total)
                  setHasAnyBalances(Object.keys(mockData.balances).length > 0)
                  setLastUpdateOrCreatedTime(mostRecentTime)
                  
                  // Set weather warning settings from mock data
                  if (mockData.settings && mockData.settings.weatherWarnings) {
                      setWeatherWarningSettings(mockData.settings.weatherWarnings)
                  }
                  
                  setLoading(false)
                  return
              }

                const userId = user.uid

                // Set up listeners for different data types
                const incomeRef = ref(database, `users/${userId}/flo/income`)
                const expenseRef = ref(database, `users/${userId}/flo/expenses`)
                const balanceRef = ref(database, `users/${userId}/flo/balances`)
                const budgetRef = ref(database, `users/${userId}/flo/budgets`)

                // Handle income data
                const incomeListener = onValue(
                    incomeRef,
                    (snapshot) => {
                        const incomeData = snapshot.val() || {}
                        const formattedIncome = formatDataItems(
                            incomeData,
                            "income"
                        )
                        updateCashFlowItems(formattedIncome)
                    },
                    (err) => {
                        setError(err.message)
                    }
                )

                // Handle expense data
                const expenseListener = onValue(
                    expenseRef,
                    (snapshot) => {
                        const expenseData = snapshot.val() || {}
                        const formattedExpenses = formatDataItems(
                            expenseData,
                            "expense"
                        )
                        updateCashFlowItems(formattedExpenses)
                    },
                    (err) => {
                        setError(err.message)
                    }
                )

                // Handle balance data
                const balanceListener = onValue(
                    balanceRef,
                    (snapshot) => {
                        const balanceData = snapshot.val() || {}
                        const formattedBalances = formatDataItems(
                            balanceData,
                            "balance"
                        )
                        updateCashFlowItems(formattedBalances)

                        // Also set balances for direct access
                        const balancesObject = {}
                        Object.entries(balanceData).forEach(([id, balance]) => {
                            balancesObject[id] = {
                                ...balance,
                                id,
                                amount: balance.amount / 100, // Convert cents to dollars for display
                            }
                        })
                        setBalances(balancesObject)

                        // Calculate total balance from accounts included in projections
                        const total = Object.values(balanceData)
                            .filter((account) => account.includeInProjections !== false)
                            .reduce(
                                (sum, account) => {
                                    return sum + (account.amount || 0)
                                },
                                0
                            )

                        // Find most recent update or creation time
                        const mostRecentTime = Object.values(
                            balanceData
                        ).reduce((latest, account) => {
                            const accountTime =
                                account.lastUpdated || account.createdAt
                            if (!latest) return accountTime
                            return new Date(accountTime) > new Date(latest)
                                ? accountTime
                                : latest
                        }, null)

                        // Update the state values needed by CurrentBalanceHeader
                        setCurrentTotalBalance(total)
                        setHasAnyBalances(Object.keys(balanceData).length > 0)
                        setLastUpdateOrCreatedTime(mostRecentTime)
                    },
                    (err) => {
                        setError(err.message)
                    }
                )

                // Handle budget data
                const budgetListener = onValue(
                    budgetRef,
                    (snapshot) => {
                        const budgetData = snapshot.val() || {}
                        const formattedBudgets = formatDataItems(
                            budgetData,
                            "budget"
                        )
                        updateCashFlowItems(formattedBudgets)
                        setBudgets(formattedBudgets)
                    },
                    (err) => {
                        setError(err.message)
                    }
                )

                setLoading(false)

                // Cleanup listeners when component unmounts
                return () => {
                    incomeListener()
                    expenseListener()
                    balanceListener()
                    budgetListener()
                }
            } catch (err) {
                setError(err.message)
                setLoading(false)
            }
        }

        fetchData()
    }, [updateCashFlowItems])

    // Calculate projected balances when cash flow items change
    useEffect(() => {
        if (cashFlowItems.length > 0) {
            calculateProjectedBalances()
        }
    }, [cashFlowItems, calculateProjectedBalances])

    const defaultBudgetCategories = {
        "-OJACLkpA2FiJSyu0vjP": {
            amount: 0,
            color: "#FF7283",
            emoji: "🏠",
            title: "Housing",
            type: "budget",
        },
        "-OJADQPMJvMQl5KBoaCc": {
            amount: 0,
            color: "#F5BF26",
            emoji: "🍽️",
            title: "Food",
            type: "budget",
        },
        "-OJADR14tUaKW9n81Jnq": {
            amount: 0,
            color: "#039BE5",
            emoji: "🚗",
            title: "Transportation",
            type: "budget",
        },
        "-OJADRmU8QSsNZoG9Cv5": {
            amount: 0,
            color: "#3F51B5",
            emoji: "💡",
            title: "Utilities",
            type: "budget",
        },
        "-OJADWsblghS5A1r2_KM": {
            amount: 0,
            color: "#8E24AA",
            emoji: "🎭",
            title: "Entertainment",
            type: "budget",
        },
        "-OJADftlmo7iSMzW6xiA": {
            amount: 0,
            color: "#5FD8D5",
            emoji: "📚",
            title: "Education",
            type: "budget",
        },
        "-OJADhEHkUPyF8djIZqY": {
            amount: 0,
            color: "#795548",
            emoji: "🏥",
            title: "Healthcare",
            type: "budget",
        },
        "-OJADiAfbiOaREufe-Ng": {
            amount: 0,
            color: "#E67C73",
            emoji: "👤",
            title: "Personal",
            type: "budget",
        },
    }

    // Add this function to useFinanceData
    const getCategoryColor = useCallback(
        (categoryId) => {
            // First check if the category exists in our real-time budgetCategories
            const budgetItem = budgets.find(
                (budget) => budget.id === categoryId
            )
            if (budgetItem && budgetItem.color) {
                return budgetItem.color
            }

            // Fall back to our hard-coded default categories
            if (
                defaultBudgetCategories[categoryId] &&
                defaultBudgetCategories[categoryId].color
            ) {
                return defaultBudgetCategories[categoryId].color
            }

            // Default color if no match is found
            return "#10b981" // A neutral gray color
        },
        [budgets]
    )

    // Add this function to useFinanceData
    const getCategoryEmoji = useCallback(
        (categoryId) => {
            // First check if the category exists in our real-time budgetCategories
            const budgetItem = budgets.find(
                (budget) => budget.id === categoryId
            )
            if (budgetItem && budgetItem.emoji) {
                return budgetItem.emoji
            }

            // Fall back to our hard-coded default categories
            if (
                defaultBudgetCategories[categoryId] &&
                defaultBudgetCategories[categoryId].emoji
            ) {
                return defaultBudgetCategories[categoryId].emoji
            }

            // Default emoji if no match is found
            return "📋" // A generic clipboard emoji
        },
        [budgets]
    )

    // Add this function to useFinanceData
    const getCategoryInfo = useCallback(
        (categoryId) => {
            // First check if the category exists in our real-time budgetCategories
            const budgetItem = budgets.find(
                (budget) => budget.id === categoryId
            )
            if (budgetItem) {
                return {
                    title: budgetItem.title,
                    color: budgetItem.color,
                    emoji: budgetItem.emoji,
                }
            }

            // Fall back to our hard-coded default categories
            if (defaultBudgetCategories[categoryId]) {
                return {
                    title: defaultBudgetCategories[categoryId].title,
                    color: defaultBudgetCategories[categoryId].color,
                    emoji: defaultBudgetCategories[categoryId].emoji,
                }
            }

            // Default values if no match is found
            return {
                title: "Uncategorized",
                color: "#9CA3AF",
                emoji: "📋",
            }
        },
        [budgets]
    )
    const getBudgetColor = useCallback(
        (budgetId) => {
            // Remove any prefixes if present (like "default-")
            const cleanId = budgetId.replace("default-", "")

            // First check if the category exists in our real-time budgets
            const budgetItem = budgets.find((budget) => budget.id === cleanId)
            if (budgetItem && budgetItem.color) {
                return budgetItem.color
            }

            // Fall back to our hard-coded default categories
            if (
                defaultBudgetCategories[cleanId] &&
                defaultBudgetCategories[cleanId].color
            ) {
                return defaultBudgetCategories[cleanId].color
            }

            // Default color if no match is found
            return "#9CA3AF" // A neutral gray color
        },
        [budgets]
    )

    // A function to get all budget categories for the picker
    const getBudgetCategories = useCallback(() => {
        return budgets.filter((item) => item.type === "budget")
    }, [budgets])
    const [weatherWarningSettings, setWeatherWarningSettings] = useState(null)
    const [selectedScale, setSelectedScale] = useState("moderate")

    // Add these configurations inside useFinanceData
    const balanceScalePresets = {
        conservative: {
            name: "Conservative",
            description: "For those who prefer keeping higher balances",
            multiplier: 1,
            baseThresholds: {
                stormWarning: 500,
                rainWarning: 1000,
                partlyCloudy: 2000,
            },
        },
        moderate: {
            name: "Moderate",
            description: "Balanced approach to cash management",
            multiplier: 1,
            baseThresholds: {
                stormWarning: 100,
                rainWarning: 200,
                partlyCloudy: 500,
            },
        },
        optimistic: {
            name: "Optimistic",
            description: "For those who feel comfortable with smaller cushions",
            multiplier: 1,
            baseThresholds: {
                stormWarning: 50,
                rainWarning: 75,
                partlyCloudy: 100,
            },
        },
    }

    // Add this function to calculate thresholds based on selected scale
    const calculateThresholds = useCallback(
        (scale) => {
            const preset =
                balanceScalePresets[scale] || balanceScalePresets.moderate
            return {
                stormWarning:
                    preset.baseThresholds.stormWarning * preset.multiplier,
                rainWarning:
                    preset.baseThresholds.rainWarning * preset.multiplier,
                partlyCloudy:
                    preset.baseThresholds.partlyCloudy * preset.multiplier,
            }
        },
        [balanceScalePresets]
    )

    // Add this function to update weather warning settings
    const updateWeatherWarningSettings = useCallback(
        async (newSettings, newScale = null) => {
            const user = auth.currentUser
            if (!user) return

            const userId = user.uid
            const weatherSettingsRef = ref(
                database,
                `users/${userId}/settings/weatherWarnings`
            )
            try {
                const updatedSettings = {
                    ...newSettings,
                    selectedScale: newScale || selectedScale,
                    thresholds: calculateThresholds(newScale || selectedScale),
                }

                await update(weatherSettingsRef, updatedSettings)
                setWeatherWarningSettings(updatedSettings)
                if (newScale) setSelectedScale(newScale)
            } catch (error) {
                console.error("Error updating weather warning settings:", error)
            }
        },
        [selectedScale, calculateThresholds]
    )

    // Add this effect to load weather warning settings
    useEffect(() => {
        const user = auth.currentUser
        if (!user) return

        const userId = user.uid
        const weatherSettingsRef = ref(
            database,
            `users/${userId}/settings/weatherWarnings`
        )
        const unsubscribe = onValue(weatherSettingsRef, (snapshot) => {
            const settingsData = snapshot.val()
            if (settingsData) {
                setWeatherWarningSettings(settingsData)
                if (settingsData.selectedScale) {
                    setSelectedScale(settingsData.selectedScale)
                }
            } else {
                // Initialize with default settings if none exist
                const defaultSettings = {
                    stormWarning: {
                        emoji: "⛈️",
                        title: "Storm Warning",
                        color: "#DC2626",
                        recommendation:
                            "Your balance is getting low. Consider adding income or reducing expenses",
                    },
                    rainWarning: {
                        emoji: "🌧️",
                        title: "Rain Expected",
                        color: "#EA580C",
                        recommendation:
                            "Keep an eye on your spending in the coming days",
                    },
                    partlyCloudy: {
                        emoji: "⛅",
                        title: "Partly Cloudy",
                        color: "#0EA5E9",
                        recommendation: null,
                    },
                    clearSkies: {
                        emoji: "☀️",
                        title: "Clear Skies",
                        color: "#22C55E",
                        recommendation: null,
                    },
                    selectedScale: "moderate",
                    thresholds: calculateThresholds("moderate"),
                }
                setWeatherWarningSettings(defaultSettings)
            }
        })

        return () => unsubscribe()
    }, [calculateThresholds])

    const getBalanceItems = useCallback(() => {
        return cashFlowItems
            .filter((item) => item.type === "balance")
            .sort((a, b) => {
                // Sort by lastUpdated or createdAt, newest first
                const dateA = a.lastUpdated || a.createdAt
                const dateB = b.lastUpdated || b.createdAt
                return new Date(dateB) - new Date(dateA)
            })
    }, [cashFlowItems])

    // Update a bank account amount
    const updateBankAccountAmount = useCallback(
        async (accountId, newAmount) => {
            if (!auth.currentUser) {
                console.error("No user logged in")
                return
            }

            try {
                const userId = auth.currentUser.uid
                const accountRef = ref(
                    database,
                    `users/${userId}/flo/balances/${accountId}`
                )

                // Convert dollars to cents and ensure it's positive
                const amountInCents = Math.round(Math.abs(newAmount) * 100)
                const timestamp = new Date().toISOString()

                // Update the account
                await update(accountRef, {
                    amount: amountInCents,
                    lastUpdated: timestamp,
                })

                // Update local state
                updateCashFlowItems([
                    {
                        id: accountId,
                        type: "balance",
                        amount: Math.abs(newAmount), // Store as positive dollars in local state
                        lastUpdated: timestamp,
                    },
                ])

                // Update current total balance
                calculateTotalBalance()

                // Recalculate projected balances
                calculateProjectedBalances()

                return true
            } catch (error) {
                console.error("Error updating bank account amount:", error)
                throw error
            }
        },
        [auth, database, updateCashFlowItems, calculateProjectedBalances]
    )

    // Calculate the total balance across all accounts
    const calculateTotalBalance = useCallback(() => {
        const balances = getBalanceItems()

        // Sum up only balances included in projections (already in dollars in our state)
        const newTotalBalance = balances
            .filter((item) => item.includeInProjections !== false)
            .reduce((total, item) => {
                return total + item.amount * 100
            }, 0)

        // Update the state values
        setCurrentTotalBalance(newTotalBalance)
        setHasAnyBalances(balances.length > 0)

        // Find the most recent update time
        const mostRecentTime = balances.reduce((latestTime, account) => {
            const accountTime = account.lastUpdated || account.createdAt
            if (!latestTime) return accountTime
            return new Date(accountTime) > new Date(latestTime)
                ? accountTime
                : latestTime
        }, null)

        setLastUpdateOrCreatedTime(mostRecentTime)

        return newTotalBalance
    }, [getBalanceItems])

    // Add a new bank account/balance
    const addBankAccount = useCallback(
        async (accountData) => {
            if (!auth.currentUser) {
                console.error("No user logged in")
                return
            }

            try {
                const userId = auth.currentUser.uid
                const timestamp = new Date().toISOString()

                // Prepare the account data
                const accountToAdd = {
                    title: accountData.title,
                    amount: Math.round(Math.abs(accountData.amount) * 100), // Convert to cents and ensure positive
                    type: "balance",
                    createdAt: timestamp,
                    lastUpdated: timestamp,
                    ...(accountData.accountType && { accountType: accountData.accountType }),
                    ...(accountData.emoji && { emoji: accountData.emoji }),
                }

                // Add to Firebase
                const balancesRef = ref(
                    database,
                    `users/${userId}/flo/balances`
                )
                const newAccountRef = await push(balancesRef, accountToAdd)

                // Create the new account object with ID
                const newAccount = {
                    ...accountToAdd,
                    id: newAccountRef.key,
                    amount: Math.abs(accountData.amount), // Keep as dollars in local state
                }

                // Update local state
                updateCashFlowItems([newAccount])

                // Update total balance
                calculateTotalBalance()

                // Recalculate projected balances
                calculateProjectedBalances()

                return newAccount
            } catch (error) {
                console.error("Error adding bank account:", error)
                throw error
            }
        },
        [
            auth,
            database,
            updateCashFlowItems,
            calculateProjectedBalances,
            calculateTotalBalance,
        ]
    )

    // Get the last update time for display
    const getLastUpdateTime = useCallback(() => {
        if (!lastUpdateOrCreatedTime) return ""

        const updateTime = new Date(lastUpdateOrCreatedTime)
        const now = new Date()

        // Calculate time difference in days
        const diffTime = Math.abs(now - updateTime)
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

        if (diffDays > 0) {
            return `Updated ${updateTime.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
            })}, ${updateTime.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            })}`
        }

        // Simple "time ago" logic
        const diffHours = Math.floor(diffTime / (1000 * 60 * 60))
        if (diffHours > 0) {
            return `Updated ${diffHours} ${
                diffHours === 1 ? "hour" : "hours"
            } ago`
        }

        const diffMinutes = Math.floor(diffTime / (1000 * 60))
        if (diffMinutes > 0) {
            return `Updated ${diffMinutes} ${
                diffMinutes === 1 ? "minute" : "minutes"
            } ago`
        }

        return "Updated just now"
    }, [lastUpdateOrCreatedTime])
    // Add these functions to the useFinanceData.js hook in the React web version

// Mark an item as paid
const markAsPaid = async (itemId, occurrenceDate, updates = {}) => {
  const user = auth.currentUser;
  if (!user) {
    console.error('No user logged in');
    return;
  }

  const item = cashFlowItems.find(item => item.id === itemId);
  if (!item) {
    console.error('Item not found');
    return;
  }

  const getItemPath = (type) => {
    switch (type) {
      case "income":
        return "income";
      case "expense":
        return "expenses";
      case "balance":
        return "balances";
      case "budget":
        return "budgets";
      case "savings":
        return "savings";
      default:
        throw new Error(`Invalid item type: ${type}`);
    }
  };

  const userId = user.uid;
  const itemRef = ref(database, `users/${userId}/flo/${getItemPath(item.type)}/${itemId}`);

  try {
    const dbUpdates = {};

    if (item.recurrencePeriod === 'none') {
      // For non-recurring items, set base isPaid to true
      dbUpdates.isPaid = true;
      if (updates.amount !== undefined) {
        // Convert dollars to cents for storage
        dbUpdates.amount = Math.round(updates.amount * 100);
      }
      if (updates.date !== undefined) {
        dbUpdates.date = updates.date;
      }
    } else {
      // For recurring items
      const originalDateStr = moment(occurrenceDate).format('YYYY-MM-DD');
      const newDateStr = moment(updates.date || occurrenceDate).format('YYYY-MM-DD');

      const exceptionData = {
        isPaid: true,
        // Convert dollars to cents for storage, ensuring we get the amount from updates or the original item
        amount: updates.amount !== undefined 
          ? Math.round(Math.abs(updates.amount) * 100)
          : Math.abs(item.amount) * 100,
        title: item.title,
        emoji: item.emoji,
        date: updates.date || occurrenceDate,
      };

      if (item.type !== 'expense' && item.type !== 'income') {
        exceptionData.color = item.color;
      }

      // Handle date changes
      if (originalDateStr !== newDateStr) {
        dbUpdates.occurrenceExceptions = {
          ...(item.occurrenceExceptions || {}),
        };
        // Remove old exception if it exists
        if (dbUpdates.occurrenceExceptions[originalDateStr]) {
          delete dbUpdates.occurrenceExceptions[originalDateStr];
        }
        // Add new exception
        dbUpdates.occurrenceExceptions[newDateStr] = exceptionData;
      } else {
        // No date change
        dbUpdates.occurrenceExceptions = {
          ...(item.occurrenceExceptions || {}),
          [originalDateStr]: exceptionData,
        };
      }
    }

    // Apply updates to Firebase
    await update(itemRef, dbUpdates);

    // Update local state
    const updatedItem = {
      ...item,
      ...dbUpdates,
      occurrenceExceptions: {
        ...(item.occurrenceExceptions || {}),
        ...(dbUpdates.occurrenceExceptions || {}),
      },
    };

    updateCashFlowItems([updatedItem]);
    calculateProjectedBalances();

    return updatedItem;
  } catch (error) {
    console.error('Error marking item as paid:', error);
    throw error;
  }
};

// Unmark an item as paid
const unmarkAsPaid = async (itemId, occurrenceDate) => {
  const user = auth.currentUser;
  if (!user) {
    console.error('No user logged in');
    return false;
  }

  // Find the item in cashFlowItems
  const item = cashFlowItems.find(item => item.id === itemId);
  if (!item) {
    console.error('Item not found');
    return false;
  }

  const getItemPath = (type) => {
    switch (type) {
      case "income":
        return "income";
      case "expense":
        return "expenses";
      case "balance":
        return "balances";
      case "budget":
        return "budgets";
      case "savings":
        return "savings";
      default:
        throw new Error(`Invalid item type: ${type}`);
    }
  };

  const userId = user.uid;
  const itemRef = ref(database, `users/${userId}/flo/${getItemPath(item.type)}/${itemId}`);

  try {
    let updates = {};

    if (item.recurrencePeriod === 'none') {
      // For non-recurring items, simply unmark as paid in the base item
      updates.isPaid = false;
    } else {
      // For recurring items, remove the occurrence exception or set isPaid to false
      const formattedDate = moment(occurrenceDate).format('YYYY-MM-DD');
      if (item.occurrenceExceptions && item.occurrenceExceptions[formattedDate]) {
        // If there's an exception, update it to unpaid
        updates[`occurrenceExceptions/${formattedDate}/isPaid`] = false;
      } else {
        // If there's no exception, we don't need to do anything for this occurrence
        console.log(`No paid exception found for ${formattedDate}, item is already unpaid`);
        return true;
      }
    }

    // Apply the update
    await update(itemRef, updates);

    // Update local state
    if (item.recurrencePeriod === 'none') {
      updateCashFlowItems([{
        ...item,
        isPaid: false
      }]);
    } else {
      const formattedDate = moment(occurrenceDate).format('YYYY-MM-DD');
      updateCashFlowItems([{
        ...item,
        occurrenceExceptions: {
          ...item.occurrenceExceptions,
          [formattedDate]: {
            ...item.occurrenceExceptions[formattedDate],
            isPaid: false,
          },
        },
      }]);
    }

    calculateProjectedBalances();
    return true;
  } catch (error) {
    console.error('Error unmarking item as paid:', error);
    return false;
  }
};

// Helper function to check if an occurrence is paid
const isPaidOccurrence = (item, date) => {
  const formattedDate = moment(date).format('YYYY-MM-DD');

  if (item.recurrenceFrequency === 'none') {
    return item.isPaid;
  }

  if (moment(formattedDate).isSame(item.date, 'day')) {
    return item.isPaid;
  }

  if (item.occurrenceExceptions && item.occurrenceExceptions[formattedDate]) {
    return item.occurrenceExceptions[formattedDate].isPaid;
  }

  if (item.editedFromDate && moment(formattedDate).isSameOrAfter(item.editedFromDate)) {
    return item.futureEdits?.isPaid || false;
  }

  return false;
};
    // ── Budget CRUD ──
    const addBudgetCategory = useCallback(async (categoryData) => {
        const user = auth.currentUser;
        if (!user) return null;
        const userId = user.uid;
        const now = new Date().toISOString();
        const budgetsRef = ref(database, `users/${userId}/flo/budgets`);
        const newRef = await push(budgetsRef, {
            title: categoryData.title,
            emoji: categoryData.emoji || '📋',
            color: categoryData.color || '#9CA3AF',
            categoryKey: categoryData.categoryKey || 'custom',
            isDefault: false,
            isArchived: false,
            amount: categoryData.amount ? Math.round(categoryData.amount * 100) : 0,
            periods: {},
            manualEntries: {},
            createdAt: now,
        });
        return { success: true, categoryId: newRef.key };
    }, []);

    const updateBudgetCategory = useCallback(async (budgetId, updates) => {
        const user = auth.currentUser;
        if (!user) return;
        const budgetRef = ref(database, `users/${user.uid}/flo/budgets/${budgetId}`);
        await update(budgetRef, updates);
    }, []);

    const updateBudgetPeriodData = useCallback(async (budgetId, periodKey, assignedAmount) => {
        const user = auth.currentUser;
        if (!user) return;
        const periodRef = ref(database, `users/${user.uid}/flo/budgets/${budgetId}/periods/${periodKey}`);
        await set(periodRef, { assigned: Math.round(assignedAmount * 100) });
    }, []);

    const deleteBudgetCategory = useCallback(async (budgetId) => {
        const user = auth.currentUser;
        if (!user) return;
        await remove(ref(database, `users/${user.uid}/flo/budgets/${budgetId}`));
    }, []);

    const archiveBudgetCategory = useCallback(async (budgetId) => {
        const user = auth.currentUser;
        if (!user) return;
        const budgetRef = ref(database, `users/${user.uid}/flo/budgets/${budgetId}`);
        await update(budgetRef, { isArchived: true, archivedAt: new Date().toISOString() });
    }, []);

    const unarchiveBudgetCategory = useCallback(async (budgetId) => {
        const user = auth.currentUser;
        if (!user) return;
        const budgetRef = ref(database, `users/${user.uid}/flo/budgets/${budgetId}`);
        await update(budgetRef, { isArchived: false, archivedAt: null });
    }, []);

    return {
      markAsPaid,
      unmarkAsPaid,
      isPaidOccurrence,
        getBalanceItems,
        updateBankAccountAmount,
        addBankAccount,
        calculateTotalBalance,
        getLastUpdateTime,
        currentTotalBalance,
        hasAnyBalances,
        lastUpdateOrCreatedTime,
        getCategoryColor,
        getCategoryEmoji,
        getCategoryInfo,
        cashFlowItems,
        balances,
        budgets,
        loading,
        error,
        projectedBalances,
        getItemsForDateRange,
        getCurrentBalance,
        formatAmount,
        calculateMonthlyIncome,
        calculateMonthlyExpenses,
        remainingIncomeAmount,
        calculateProjectedBalances,
        defaultBudgetCategories,
        addItem,
        editItem,
        deleteItem,
        // Add these new return values to match the React Native version
        currentTotalBalance,
        hasAnyBalances,
        lastUpdateOrCreatedTime,
        getBudgetColor,
        getBudgetCategories,
        addBudgetCategory,
        updateBudgetCategory,
        updateBudgetPeriodData,
        deleteBudgetCategory,
        archiveBudgetCategory,
        unarchiveBudgetCategory,
        weatherWarningSettings,
        updateWeatherWarningSettings,
        selectedScale,
        balanceScalePresets,
        calculateThresholds,
    }
}